import React, { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Collapse,
  Container,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import FlexiblePlansTab from './Tabs/FlexiblePlansTab';
import { checkUserPermission } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';
import SubscriptionPlansTab from './Tabs/SubscriptionPlansTab';
import GroupsTab from './Tabs/GroupsTab';
import CoursePermissionTemplatesTab from './Tabs/CoursePermissionTemplatesTab';
import StudentFlexibleRatesTab from './Tabs/StudentFlexibleRatesTab';
import StudentSubscriptionRatesTab from './Tabs/StudentSubscriptionRatesTab';
import CoursePricingTemplatesTab from './Tabs/CoursePricingTemplatesTab';
import RolesTab from './Tabs/RolesTab';
import TransactionLogsTab from './Tabs/TransactionLogsTab';
import PaymentHistoryTab from './Tabs/PaymentHistoryTab';
import InvoicesTab from './Tabs/InvoicesTab';
import AdjustmentsTab from './Tabs/AdjustmentsTab';
import OneToOneTutorRatesTab from './Tabs/OneToOneTutorRatesTab';
import GroupTutorRatesTab from './Tabs/GroupTutorRatesTab';
import MetaTagsTab from './Tabs/MetaTagsTab';
import RedirectionsTab from './Tabs/RedirectionsTab';
import SitemapTab from './Tabs/SitemapTab';
import CourseTemplatesTab from './Tabs/CourseTemplatesTab';

type TMenuOption = {
  key: string;
  title: string;
  options?: TMenuOption[];
};

type TState = {
  selectedMenuOption: TMenuOption | null;
  openedMenus: string[];
};

const navigationBarWidth = 240;
const menuOptions: TMenuOption[] = [
  {
    key: 'finance',
    title: 'Finance',
    options: [
      {
        key: 'flexiblePlans',
        title: 'Flexible Plans',
      },
      {
        key: 'subscriptionPlans',
        title: 'Subscription Plans',
      },
      {
        key: 'studentRates',
        title: 'Student Rates',
        options: [
          {
            key: 'studentFlexibleRates',
            title: 'Flexible',
          },
          {
            key: 'studentSubscriptionRates',
            title: 'Subscription',
          },
        ],
      },
      {
        key: 'tutorRates',
        title: 'Tutor Rates',
        options: [
          {
            key: 'oneToOneTutorRates',
            title: 'One to One',
          },
          {
            key: 'groupTutorRates',
            title: 'Group',
          },
        ],
      },
      {
        key: 'transactionLogs',
        title: 'Transaction Logs',
      },
      {
        key: 'paymentHistory',
        title: 'Payment Histories',
      },
      {
        key: 'invoices',
        title: 'Invoices',
      },
      {
        key: 'adjustments',
        title: 'Adjustments',
      },
    ],
  },
  {
    key: 'user',
    title: 'User',
    options: [
      {
        key: 'groups',
        title: 'Groups',
      },
      {
        key: 'roles',
        title: 'Roles',
      },
    ],
  },
  {
    key: 'course',
    title: 'Course',
    options: [
      {
        key: 'courseTemplates',
        title: 'Templates',
      },
      {
        key: 'permissionTemplates',
        title: 'Permission Templates',
      },
      {
        key: 'pricingTemplates',
        title: 'Pricing Templates',
      },
    ],
  },
  {
    key: 'seo',
    title: 'SEO',
    options: [
      {
        key: 'metaTags',
        title: 'Meta Tags',
      },
      {
        key: 'redirections',
        title: 'Redirections',
      },
      {
        key: 'sitemap',
        title: 'Sitemap',
      },
    ],
  },
];

const getMatchingOptionData = (
  options: TMenuOption[],
  key: string,
  path: string[] = []
): { option?: TMenuOption; path: string[] } => {
  // eslint-disable-next-line no-restricted-syntax
  for (const option of options) {
    const newPath = [...path, option.key];

    if (option.key === key) {
      return { option, path: newPath };
    }

    if (option.options) {
      const result = getMatchingOptionData(option.options, key, newPath);
      if (result.option) return result;
    }
  }

  return { path: [] };
};

const initialState: TState = {
  selectedMenuOption: null,
  openedMenus: [],
};

const AdminSettingsPage: FunctionComponent = () => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState({ ...initialState });

  const authUser = authState.user;
  const selectedTabUrlValue = searchParams.get('selectedTab') || '';

  useEffect(() => {
    if (selectedTabUrlValue) {
      const matchingOptionData = getMatchingOptionData(
        menuOptions,
        selectedTabUrlValue
      );

      setState((prevState) => {
        const newStateData = { ...prevState };

        let newOpenedMenus = [...newStateData.openedMenus];

        if (newOpenedMenus.includes(selectedTabUrlValue)) {
          newOpenedMenus = newOpenedMenus.filter(
            (item) => item !== selectedTabUrlValue
          );
        } else {
          newOpenedMenus.push(selectedTabUrlValue);
        }

        newStateData.openedMenus = newOpenedMenus;

        if (matchingOptionData.option) {
          newStateData.selectedMenuOption = matchingOptionData.option;
          newStateData.openedMenus = matchingOptionData.path || [];
        }

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onSubMenuChange = (key: string) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      let newOpenedMenus = [...newStateData.openedMenus];

      if (newOpenedMenus.includes(key)) {
        newOpenedMenus = newOpenedMenus.filter((item) => item !== key);
      } else {
        newOpenedMenus.push(key);
      }

      newStateData.openedMenus = newOpenedMenus;

      return newStateData;
    });
  };

  const checkTabPermission = (option: TMenuOption) => {
    let hasTabPermission = true;

    switch (option.key) {
      case 'finance':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ADJUSTMENTS_CREATE,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_VIEW,
          ],
        });
        break;

      case 'flexiblePlans':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW,
          ],
        });
        break;

      case 'subscriptionPlans':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW,
          ],
        });
        break;

      case 'studentFlexibleRates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_VIEW,
          ],
        });
        break;

      case 'studentSubscriptionRates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_VIEW,
          ],
        });
        break;

      case 'tutorRates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_VIEW,
          ],
        });
        break;

      case 'oneToOneTutorRates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_VIEW,
          ],
        });
        break;

      case 'groupTutorRates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_VIEW,
          ],
        });
        break;

      case 'transactionLogs':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW,
          ],
        });
        break;

      case 'paymentHistory':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW,
          ],
        });
        break;

      case 'invoices':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_VIEW,
          ],
        });
        break;

      case 'adjustments':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ADJUSTMENTS_CREATE,
          ],
        });
        break;

      case 'user':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ROLES_VIEW,
          ],
        });
        break;

      case 'groups':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_VIEW,
          ],
        });
        break;

      case 'roles':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ROLES_VIEW,
          ],
        });
        break;

      case 'course':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW,
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW,
          ],
          adminCourseTemplate: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
          ],
        });
        break;

      case 'permissionTemplates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW,
          ],
        });
        break;

      case 'pricingTemplates':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW,
          ],
        });
        break;

      case 'metaTags':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_META_TAGS_VIEW,
          ],
        });
        break;

      case 'redirections':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_REDIRECTIONS_VIEW,
          ],
        });
        break;

      case 'sitemap':
        hasTabPermission = checkUserPermission(authUser, {
          adminSettings: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SITEMAP_VIEW,
          ],
        });
        break;

      case 'courseTemplates':
        hasTabPermission = checkUserPermission(authUser, {
          adminCourseTemplate: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
          ],
        });
        break;

      default:
        hasTabPermission = true;
        break;
    }

    return hasTabPermission;
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'text',
        title: 'Settings',
        highlight: true,
      },
    ];

    return options;
  };

  const renderMenus = ({
    list,
    parent,
    level = 0,
  }: {
    list: TMenuOption[];
    level: number;
    parent?: TMenuOption;
  }) => {
    return (
      <List disablePadding>
        {list.map((option) => {
          const hasTabPermission = checkTabPermission(option);
          const isSubMenuOpened = state.openedMenus.includes(option.key);
          const hasSubMenus = (option.options || []).length > 0;
          const isSelected = Boolean(
            state.selectedMenuOption &&
              state.selectedMenuOption.key === option.key
          );

          const renderArrows = () => {
            if (hasSubMenus) {
              return state.openedMenus.includes(option.key) ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              );
            }

            return null;
          };

          if (!hasTabPermission) {
            return null;
          }

          let subMenus: JSX.Element | null = null;

          if (hasSubMenus) {
            subMenus = renderMenus({
              list: option.options || [],
              parent: option,
              level: level + 1,
            });
          }

          return [
            <ListItem
              key={`${option.key}-menu`}
              disablePadding
              onClick={() => {
                if (hasSubMenus) {
                  onSubMenuChange(option.key);
                } else {
                  const currentParams = Object.fromEntries(
                    searchParams.entries()
                  );

                  const updatedParams = {
                    ...currentParams,
                    selectedTab: option.key,
                  };

                  const queryString = new URLSearchParams(
                    updatedParams
                  ).toString();

                  navigate(`?${queryString}`, { replace: true });

                  updateStateAttributes({ selectedMenuOption: option });
                }
              }}
            >
              <ListItemButton
                selected={isSelected}
                sx={{ pl: parent ? level * 4 : undefined }}
              >
                <ListItemText primary={option.title} />
                {renderArrows()}
              </ListItemButton>
            </ListItem>,
            <Collapse key={`${option.key}-collapse`} in={isSubMenuOpened}>
              {subMenus}
            </Collapse>,
          ];
        })}
      </List>
    );
  };

  const renderOptionContent = () => {
    let optionContent: JSX.Element | null = null;

    if (state.selectedMenuOption) {
      switch (state.selectedMenuOption.key) {
        case 'flexiblePlans':
          optionContent = <FlexiblePlansTab />;
          break;

        case 'subscriptionPlans':
          optionContent = <SubscriptionPlansTab />;
          break;

        case 'groups':
          optionContent = <GroupsTab />;
          break;

        case 'roles':
          optionContent = <RolesTab />;
          break;

        case 'permissionTemplates':
          optionContent = <CoursePermissionTemplatesTab />;
          break;

        case 'studentFlexibleRates':
          optionContent = <StudentFlexibleRatesTab />;
          break;

        case 'studentSubscriptionRates':
          optionContent = <StudentSubscriptionRatesTab />;
          break;

        case 'oneToOneTutorRates':
          optionContent = <OneToOneTutorRatesTab />;
          break;

        case 'groupTutorRates':
          optionContent = <GroupTutorRatesTab />;
          break;

        case 'transactionLogs':
          optionContent = <TransactionLogsTab />;
          break;

        case 'pricingTemplates':
          optionContent = <CoursePricingTemplatesTab />;
          break;

        case 'paymentHistory':
          optionContent = <PaymentHistoryTab />;
          break;

        case 'invoices':
          optionContent = <InvoicesTab />;
          break;

        case 'adjustments':
          optionContent = (
            <AdjustmentsTab
              onClose={() =>
                updateStateAttributes({ selectedMenuOption: null })
              }
            />
          );
          break;

        case 'metaTags':
          optionContent = <MetaTagsTab />;
          break;

        case 'redirections':
          optionContent = <RedirectionsTab />;
          break;

        case 'sitemap':
          optionContent = <SitemapTab />;
          break;

        case 'courseTemplates':
          optionContent = <CourseTemplatesTab />;
          break;

        default:
          optionContent = null;
          break;
      }
    }

    return optionContent;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <div>
      <Container maxWidth="xl">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        <div>
          <Stack direction="row" gap="20px">
            <Stack sx={{ width: `${navigationBarWidth}px` }}>
              <Box
                component="nav"
                sx={{
                  position: 'sticky',
                  top: '64px',
                  borderRight: { xs: 'none', sm: '1px solid #E2E8F0' },
                }}
              >
                {renderMenus({
                  list: menuOptions,
                  level: 0,
                })}
              </Box>
            </Stack>
            <Stack
              sx={{
                width: '100%',
                maxWidth: `calc(100% - ${navigationBarWidth}px)`,
              }}
            >
              <div>{renderOptionContent()}</div>
            </Stack>
          </Stack>
        </div>
      </Container>
    </div>
  );
};

export default AdminSettingsPage;
